'use client';
import { RefObject, useEffect, useState } from 'react';

/**
 * At least half the block on screen counts as an `element_impression` —
 * a one-pixel sliver must not report one (CMS-1976).
 */
export const ELEMENT_IMPRESSION_THRESHOLD = 0.5;

/**
 * A viewed impression (`action: 'view'`, A&A #2564) requires the block to hold
 * the threshold above for at least this long.
 */
export const ELEMENT_IMPRESSION_VIEW_DWELL_MS = 1000;

/** Shared viewport gate for blocks that send `element_impression`, so the event means the same thing everywhere. */
export const useElementInView = (
  ref: RefObject<Element | null>,
  threshold: number = ELEMENT_IMPRESSION_THRESHOLD,
): boolean => {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return inView;
};
